// QuickBitesSection — "Sprint" themed section for 80-minute AI Quick Bite courses.
//
// Visual identity:
//   • Neon lime/yellow accent (#a3e635 lime · #facc15 yellow)
//   • ⚡ lightning bolt iconography
//   • Compact horizontal-scroll cards (different vibe vs flagship grid)
//   • Coming Soon badge + "Notify me" email capture
//   • "Pick 5 for ₹1,999" mini-bundle CTA
//
// Backend endpoints:
//   GET  /api/quick-bites/courses
//   POST /api/quick-bites/bundle/quote
//   POST /api/quick-bites/bundle/checkout
//   POST /api/quick-bites/notify-me

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Zap, Clock, BookOpen, Sparkles, Award, ChevronRight, Bell, CheckCircle2, Play } from "lucide-react";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Sprint theme tokens
const LIME = "#a3e635";
const YELLOW = "#facc15";
const LIME_DEEP = "#65a30d";

function getAuthConfig() {
  const tok = localStorage.getItem("ai_tools_token");
  return tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
}

export default function QuickBitesSection() {
  const [data, setData] = useState({ items: [], live_count: 0, coming_soon_count: 0, mini_bundle: null });
  const [loading, setLoading] = useState(true);
  const [showBundle, setShowBundle] = useState(false);

  useEffect(() => {
    axios.get(`${API}/quick-bites/courses`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], mini_bundle: null }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data.items || data.items.length === 0) return null;

  const mb = data.mini_bundle || {};

  return (
    <section
      className="relative rounded-3xl overflow-hidden border border-lime-500/30"
      data-testid="quick-bites-section"
      style={{
        background:
          "radial-gradient(900px 380px at 0% 0%, rgba(163,230,53,0.10), transparent 60%), radial-gradient(700px 280px at 100% 100%, rgba(250,204,21,0.08), transparent 65%), #0a0a0a",
      }}
    >
      {/* Top header band */}
      <div className="p-5 sm:p-7 border-b border-lime-500/15 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border"
            style={{ background: "rgba(163,230,53,0.12)", borderColor: "rgba(163,230,53,0.35)" }}
          >
            <Zap size={22} fill={LIME} className="text-lime-300" />
          </div>
          <div className="min-w-0">
            <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-extrabold mb-1" style={{ color: LIME }}>
              <Zap size={11} fill={LIME} /> Quick Bites · 80-min Sprints
            </p>
            <p className="text-white text-xl sm:text-2xl font-extrabold leading-tight">
              Finish a course before your coffee gets cold.
            </p>
            <p className="text-zinc-400 text-sm mt-1">
              20-min modules. {data.live_count > 0 ? `${data.live_count} live` : "All launching soon"} ·
              {data.coming_soon_count > 0 ? ` ${data.coming_soon_count} dropping soon ` : ""}
              · starting at <span className="text-lime-300 font-bold">₹399</span>.
            </p>
          </div>
        </div>
        {/* Mini-bundle CTA */}
        {mb.base_count && (
          <button
            onClick={() => setShowBundle(true)}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 px-4 sm:px-5 py-3 rounded-xl font-extrabold text-sm whitespace-nowrap text-black hover:scale-[1.02] transition-transform"
            style={{ background: `linear-gradient(135deg, ${LIME}, ${YELLOW})`, boxShadow: "0 12px 30px -10px rgba(163,230,53,0.55)" }}
            data-testid="qb-mini-bundle-cta"
          >
            <Zap size={14} fill="currentColor" /> Pick {mb.base_count} for ₹{mb.base_price_inr.toLocaleString("en-IN")}
            <ChevronRight size={14} />
          </button>
        )}
      </div>

      {/* Responsive card grid — all 8 visible on desktop (lg=4×2, md=3, sm=2, xs=1) */}
      <div className="px-3 sm:px-5 py-5 sm:py-6">
        <div
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          data-testid="qb-cards-grid"
        >
          {data.items.map((c) => <QuickBiteCard key={c.slug} course={c} />)}
        </div>
      </div>

      {/* Decorative diagonal bolt watermark */}
      <Zap
        size={220}
        className="absolute -right-6 -bottom-12 text-lime-500/[0.05] pointer-events-none rotate-12"
        strokeWidth={1.2}
        aria-hidden
      />

      {showBundle && (
        <QuickBitesBundleModal
          allCourses={data.items}
          miniBundle={mb}
          onClose={() => setShowBundle(false)}
        />
      )}
    </section>
  );
}

// ─── Individual Quick Bite card (compact, sprint-styled) ────────────────
function QuickBiteCard({ course: c }) {
  const isLive = c.release_status === "live";
  const url = isLive ? `/courses/${c.slug}` : null;

  const card = (
    <div
      className="rounded-2xl overflow-hidden border bg-zinc-950/60 hover:bg-zinc-900 transition-all flex flex-col"
      style={{
        borderColor: isLive ? "rgba(163,230,53,0.30)" : "rgba(82,82,91,0.50)",
        boxShadow: isLive ? "0 8px 24px -12px rgba(163,230,53,0.30)" : "none",
      }}
      data-testid={`qb-card-${c.slug}`}
    >
      {/* Top stripe */}
      <div
        className="aspect-[16/9] relative flex items-center justify-center overflow-hidden"
        style={{
          background: isLive
            ? `linear-gradient(135deg, rgba(163,230,53,0.18), rgba(250,204,21,0.08))`
            : "linear-gradient(135deg, rgba(82,82,91,0.25), rgba(39,39,42,0.20))",
        }}
      >
        {c.cover_image_url ? (
          <img src={c.cover_image_url} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="text-center">
            <Zap size={36} fill={isLive ? LIME : "#71717a"} className={isLive ? "text-lime-300" : "text-zinc-500"} />
            <p className="text-[10px] uppercase tracking-widest font-bold mt-1.5" style={{ color: isLive ? LIME : "#71717a" }}>
              {c.duration_min_total || 80} min sprint
            </p>
          </div>
        )}
        {/* Status badge */}
        <div className="absolute top-2 left-2">
          {isLive ? (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider text-black" style={{ background: LIME }}>
              ⚡ Live
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full bg-zinc-900/90 border border-zinc-700 text-zinc-300 text-[9px] font-extrabold uppercase tracking-wider">
              Coming Soon
            </span>
          )}
        </div>
        {/* Price tag */}
        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-sm border border-white/15 text-white text-[10px] font-extrabold">
          ₹{c.price_inr}
        </div>
      </div>

      <div className="p-3.5 flex flex-col flex-1">
        <p className="text-white font-bold text-[15px] leading-tight line-clamp-2 mb-1.5">{c.title}</p>
        {c.summary && <p className="text-zinc-400 text-[12px] leading-snug line-clamp-2">{c.summary}</p>}

        <div className="flex items-center gap-2.5 text-[10px] text-zinc-500 mt-3 flex-wrap">
          <span className="inline-flex items-center gap-1"><BookOpen size={10} /> {c.modules_count || 4} modules</span>
          <span className="inline-flex items-center gap-1"><Clock size={10} /> {c.duration_min_total || 80} min</span>
          <span className="inline-flex items-center gap-1"><Award size={10} /> Certified</span>
          <span className="inline-flex items-center gap-1 text-lime-300/80"><Play size={10} /> Self-paced</span>
        </div>

        <div className="mt-3 pt-3 border-t border-zinc-800/80 flex items-center justify-between">
          {c.original_price_inr && c.original_price_inr > c.price_inr ? (
            <p className="text-zinc-600 text-xs line-through">₹{c.original_price_inr}</p>
          ) : <span />}
          {isLive ? (
            <span className="text-lime-300 text-xs font-bold inline-flex items-center gap-0.5">
              Start <ChevronRight size={12} />
            </span>
          ) : (
            <NotifyMePill slug={c.slug} />
          )}
        </div>
      </div>
    </div>
  );

  return url ? <Link to={url} className="block">{card}</Link> : card;
}

// ─── "Notify me" inline email capture for coming_soon QB ────────────
function NotifyMePill({ slug }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [err, setErr] = useState("");

  async function submit(ev) {
    ev.preventDefault();
    ev.stopPropagation();
    setErr("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setErr("Please enter a valid email");
      return;
    }
    setState("submitting");
    try {
      await axios.post(`${API}/quick-bites/notify-me`, { slug, email: email.trim().toLowerCase() });
      setState("done");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't subscribe. Try again.");
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-bold" data-testid={`qb-notify-done-${slug}`}>
        <CheckCircle2 size={12} /> You&apos;ll be notified!
      </span>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className="text-yellow-300 hover:text-yellow-200 text-xs font-bold inline-flex items-center gap-1"
        data-testid={`qb-notify-btn-${slug}`}
      >
        <Bell size={11} /> Notify me
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="flex items-center gap-1 w-full" data-testid={`qb-notify-form-${slug}`}>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@email.com"
        className="flex-1 min-w-0 bg-zinc-900 border border-zinc-700 rounded-md px-2 py-1 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-yellow-400/60"
        onClick={(e) => e.preventDefault()}
        autoFocus
      />
      <button
        type="submit"
        disabled={state === "submitting"}
        className="px-2 py-1 rounded-md text-black text-[11px] font-bold disabled:opacity-50"
        style={{ background: YELLOW }}
      >
        →
      </button>
      {err && <span className="absolute -bottom-4 left-0 text-[10px] text-red-300">{err}</span>}
    </form>
  );
}

// ─── Mini-bundle modal ─────────────────────────────────────────────
function QuickBitesBundleModal({ allCourses, miniBundle, onClose }) {
  const [step, setStep] = useState(1); // 1 = pick, 2 = details
  const initial = useMemo(() => new Set((allCourses || []).slice(0, miniBundle.base_count || 5).map((c) => c.slug)), [allCourses, miniBundle]);
  const [selected, setSelected] = useState(initial);
  const [quote, setQuote] = useState({
    course_count: 0, individual_total_inr: 0, original_total_inr: 0,
    bundle_price_inr: 0, savings_inr: 0, savings_vs_original_inr: 0,
    discount_pct: 0, bundle_active: false, needs_more: 0, courses: [],
  });
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Recompute quote on every selection change
  useEffect(() => {
    const slugs = Array.from(selected);
    const handle = setTimeout(() => {
      if (slugs.length === 0) {
        setQuote({ course_count: 0, individual_total_inr: 0, original_total_inr: 0, bundle_price_inr: 0, savings_inr: 0, savings_vs_original_inr: 0, discount_pct: 0, bundle_active: false, needs_more: miniBundle.base_count || 5, courses: [] });
        return;
      }
      setQuoteLoading(true);
      axios.post(`${API}/quick-bites/bundle/quote`, { slugs })
        .then((r) => setQuote(r.data))
        .catch(() => {})
        .finally(() => setQuoteLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [selected, miniBundle.base_count]);

  function toggle(slug) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  async function payAndRegister() {
    setErr("");
    if (selected.size < (miniBundle.base_count || 5)) {
      setErr(`Mini-bundle needs ≥${miniBundle.base_count || 5} Quick Bites.`); return;
    }
    if (!form.full_name.trim() || form.full_name.trim().length < 2) {
      setErr("Please enter your full name (printed on certificates)."); return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setErr("Please enter a valid email address."); return;
    }
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10) { setErr("Please enter a 10-digit mobile number."); return; }

    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/quick-bites/bundle/checkout`, {
        slugs: Array.from(selected),
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: digits,
        origin_url: window.location.origin,
      }, getAuthConfig());
      if (!r.data?.payment_session_id) throw new Error("Payment session not created");
      const cashfree = await loadCashfree({ mode: "production" });
      cashfree.checkout({
        paymentSessionId: r.data.payment_session_id,
        redirectTarget: "_self",
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Checkout failed. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="qb-bundle-modal"
    >
      <div
        className="bg-zinc-950 border border-lime-500/30 rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl shadow-lime-500/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-lime-500/15 flex items-start justify-between gap-3" style={{ background: `linear-gradient(135deg, rgba(163,230,53,0.10), rgba(250,204,21,0.04))` }}>
          <div className="min-w-0">
            <p className="text-lime-300 text-[10px] uppercase tracking-[0.2em] font-extrabold inline-flex items-center gap-1 mb-1">
              <Zap size={11} fill={LIME} /> Step {step} of 2 · {step === 1 ? "Pick your Quick Bites" : "Your details"}
            </p>
            <p className="text-white text-xl sm:text-2xl font-extrabold leading-tight">
              Pick {miniBundle.base_count} Quick Bites for ₹{miniBundle.base_price_inr?.toLocaleString("en-IN")}
            </p>
            <p className="text-zinc-400 text-sm mt-1">
              Add more for just ₹{miniBundle.extra_price_inr} each · All certificates included · Coming-soon courses unlock the day they launch.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white text-3xl leading-none shrink-0" data-testid="qb-bundle-modal-close" aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-3">
          {step === 1 && (
            <>
              <p className="text-zinc-300 text-sm font-semibold flex items-center justify-between">
                <span>Available Quick Bites ({allCourses.length})</span>
                <span className="text-lime-300 text-xs">{selected.size} selected</span>
              </p>
              <div className="space-y-2">
                {allCourses.map((c) => {
                  const checked = selected.has(c.slug);
                  const coming = c.release_status === "coming_soon";
                  return (
                    <label
                      key={c.slug}
                      className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition-all ${
                        checked
                          ? "border-lime-500/60 bg-lime-500/[0.06]"
                          : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                      }`}
                      data-testid={`qb-bundle-row-${c.slug}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(c.slug)}
                        className="w-4 h-4 accent-lime-500 shrink-0"
                        data-testid={`qb-bundle-checkbox-${c.slug}`}
                      />
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 border"
                        style={{
                          background: checked ? "rgba(163,230,53,0.12)" : "rgba(82,82,91,0.20)",
                          borderColor: checked ? "rgba(163,230,53,0.30)" : "rgba(82,82,91,0.40)",
                        }}
                      >
                        <Zap size={20} fill={checked ? LIME : "#71717a"} className={checked ? "text-lime-300" : "text-zinc-500"} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white text-sm font-bold leading-tight">{c.title}</p>
                          {coming && (
                            <span className="px-1.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 text-[9px] uppercase font-bold tracking-wider">soon</span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-[11px] mt-0.5">
                          {c.modules_count || 4} modules · {c.duration_min_total || 80} min · Certified
                        </p>
                      </div>
                      <p className="text-zinc-300 text-sm font-bold shrink-0">₹{(c.price_inr || 0).toLocaleString("en-IN")}</p>
                    </label>
                  );
                })}
              </div>
              {/* Soft nudge to fill to 5 */}
              {selected.size > 0 && selected.size < (miniBundle.base_count || 5) && (
                <div className="rounded-xl border border-yellow-500/30 bg-yellow-500/[0.06] p-3 text-yellow-200 text-sm font-semibold flex items-center gap-2" data-testid="qb-bundle-nudge">
                  <Zap size={14} fill={YELLOW} />
                  Add {(miniBundle.base_count || 5) - selected.size} more to unlock the ₹{miniBundle.base_price_inr?.toLocaleString("en-IN")} mini-bundle.
                </div>
              )}
            </>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
                <p className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Zap size={12} fill={LIME} /> Your bundle ({quote.course_count} Quick Bites)
                </p>
                <ul className="space-y-1.5">
                  {(quote.courses || []).map((c) => (
                    <li key={c.slug} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-200 truncate pr-2 flex items-center gap-1.5">
                        {c.title}
                        {c.release_status === "coming_soon" && (
                          <span className="text-[9px] uppercase font-bold tracking-wider text-yellow-300/80">soon</span>
                        )}
                      </span>
                      <span className="text-zinc-500 line-through text-xs shrink-0">₹{(c.original_price_inr || c.price_inr || 0).toLocaleString("en-IN")}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-3">
                <Field label="Full name (as it should appear on certificates)" data-testid="qb-bundle-name-input">
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                    placeholder="e.g. Priya Sharma"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-lime-500/60"
                    data-testid="qb-bundle-name"
                    autoFocus
                  />
                </Field>
                <Field label="Email (course access + certificates)">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-lime-500/60"
                    data-testid="qb-bundle-email"
                  />
                </Field>
                <Field label="Mobile number (10 digits)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="9876543210"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-lime-500/60"
                    data-testid="qb-bundle-phone"
                  />
                </Field>
              </div>

              {err && (
                <p className="text-red-300 text-xs bg-red-900/20 border border-red-500/30 rounded-lg p-2.5" data-testid="qb-bundle-error">{err}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-lime-500/15 bg-zinc-950 p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-bold">
                {quote.course_count > 0 ? "Total" : "Pick Quick Bites"}
              </p>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-2xl sm:text-3xl font-extrabold text-lime-300" data-testid="qb-bundle-total">
                  ₹{(quote.bundle_price_inr || 0).toLocaleString("en-IN")}
                </span>
                {quote.savings_vs_original_inr > 0 && (
                  <>
                    <span className="text-zinc-500 text-sm line-through">₹{quote.original_total_inr.toLocaleString("en-IN")}</span>
                    <span className="px-2 py-0.5 rounded-full text-black text-[10px] font-extrabold whitespace-nowrap" style={{ background: YELLOW }}>
                      save ₹{quote.savings_vs_original_inr.toLocaleString("en-IN")} · {quote.discount_pct}% off
                    </span>
                  </>
                )}
                {quoteLoading && <span className="text-zinc-500 text-xs">updating…</span>}
              </div>
            </div>

            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={selected.size < (miniBundle.base_count || 5)}
                className="px-5 py-2.5 rounded-lg text-black font-extrabold text-sm disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                style={{ background: `linear-gradient(135deg, ${LIME}, ${YELLOW})` }}
                data-testid="qb-bundle-continue-btn"
              >
                {selected.size < (miniBundle.base_count || 5) ? `Pick ${(miniBundle.base_count || 5) - selected.size} more →` : `Continue →`}
              </button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  className="px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-500 text-sm font-semibold disabled:opacity-50"
                  data-testid="qb-bundle-back-btn"
                >
                  ← Back
                </button>
                <button
                  onClick={payAndRegister}
                  disabled={submitting || selected.size < (miniBundle.base_count || 5)}
                  className="px-5 py-2.5 rounded-lg text-black font-extrabold text-sm disabled:opacity-50 whitespace-nowrap"
                  style={{ background: `linear-gradient(135deg, ${LIME}, ${YELLOW})` }}
                  data-testid="qb-bundle-pay-btn"
                >
                  {submitting ? "Redirecting…" : `Pay & Lock In · ₹${(quote.bundle_price_inr || 0).toLocaleString("en-IN")}`}
                </button>
              </div>
            )}
          </div>
          {step === 2 && (
            <p className="text-zinc-500 text-[11px]">
              Secure payment via Cashfree · UPI / cards / netbanking accepted · One email with magic links for all {quote.course_count} Quick Bites.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-zinc-300 text-xs font-bold mb-1.5">{label}</label>
      {children}
    </div>
  );
}
